import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';

interface LogoUploadProps {
  currentLogo?: string | null;
  onLogoChange: (file: File | null, logoUrl?: string) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogo,
  onLogoChange,
  size = 'md',
  className = '',
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogo || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tailles du composant
  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-32 h-32',
    lg: 'w-40 h-40'
  };

  const sizeClassesPreview = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide (JPG, PNG, GIF, etc.)');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La taille du fichier ne doit pas dépasser 5MB');
      return;
    }

    // Créer l'URL de prévisualisation
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onLogoChange(file, url);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    setPreviewUrl(null);
    onLogoChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Logo
      </label>
      
      <div className="space-y-3">
        {/* Zone de prévisualisation */}
        {previewUrl && (
          <div className="flex items-center space-x-3">
            <div className={`${sizeClassesPreview[size]} relative rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center`}>
              <img
                src={previewUrl}
                alt="Logo prévisualisé"
                className="w-full h-full object-contain"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  title="Supprimer le logo"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-sm text-gray-600">
              <p className="font-medium">Logo sélectionné</p>
              <p className="text-xs text-gray-500">Cliquez sur la croix pour le supprimer</p>
            </div>
          </div>
        )}

        {/* Zone d'upload */}
        <div
          className={`
            ${sizeClasses[size]} 
            border-2 border-dashed rounded-lg 
            flex flex-col items-center justify-center 
            cursor-pointer transition-all duration-200
            ${dragActive 
              ? 'border-orange-400 bg-orange-50' 
              : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${previewUrl ? 'hidden' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
          
          <div className="text-center">
            <Upload className={`mx-auto text-gray-400 mb-2 ${size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-6 h-6' : 'w-5 h-5'}`} />
            <p className="text-sm text-gray-600 font-medium">
              {dragActive ? 'Déposez le logo ici' : 'Cliquez ou glissez un logo'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              JPG, PNG, GIF (max 5MB)
            </p>
          </div>
        </div>

        {/* Bouton d'upload alternatif */}
        {previewUrl && !disabled && (
          <button
            type="button"
            onClick={openFileDialog}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Changer le logo
          </button>
        )}
      </div>
    </div>
  );
};

export default LogoUpload;
